import { message } from 'antd'
import dayjs from 'dayjs'
import { createRFP, editRFP, handleRfpAttachments, fetchRFPDraftById } from '../../../api/rfpApiService/rfpApiUtils'
import { fetchVendorList } from '../../../api/vendorApiService/vendorApiUtils'
import { getSymbol, isValidCurrencyCode } from '../../../utils/currencyUtils'

// ---------------------------------------------------------------------------
// Attachment list mapping (backend attachment records -> antd Upload items)
// ---------------------------------------------------------------------------
export const mapAttachmentsToUploadList = (attachments) => {
  return (attachments || []).map((attachment, index) => {
    if (typeof attachment === 'string') {
      return {
        uid: `saved-attachment-${index}`,
        name: attachment.split('/').pop() || `Attachment ${index + 1}`,
        status: 'done',
        url: attachment
      }
    }

    return {
      ...attachment,
      uid: attachment.uid || attachment._id || `saved-attachment-${index}`,
      name: attachment.name || attachment.fileName || attachment.filename || `Attachment ${index + 1}`,
      status: attachment.status || 'done',
      url: attachment.url || attachment.fileUrl || attachment.location
    }
  })
}

// ---------------------------------------------------------------------------
// Immediate attachment persistence (edit-draft mode only)
// ---------------------------------------------------------------------------
export const uploadAttachmentImmediately = async (editingDraft, file, msalInstance) => {
  // handleAttachments requires the Mongo _id (24-char hex), not the human-readable transactionId
  const txnId = editingDraft._id || editingDraft.transactionId
  if (!txnId) throw new Error('Transaction ID not found in draft data')

  await handleRfpAttachments(txnId, 'update', null, file, msalInstance)
  const response = await fetchRFPDraftById(msalInstance, editingDraft.transactionId || txnId)
  return mapAttachmentsToUploadList(response?.data?.attachments || [])
}

export const deleteAttachmentImmediately = async (editingDraft, attachmentId, msalInstance) => {
  // handleAttachments requires the Mongo _id (24-char hex), not the human-readable transactionId
  const txnId = editingDraft._id || editingDraft.transactionId
  if (!txnId) throw new Error('Transaction ID not found in draft data')

  await handleRfpAttachments(txnId, 'delete', attachmentId, null, msalInstance)
  const response = await fetchRFPDraftById(msalInstance, editingDraft.transactionId || txnId)
  return mapAttachmentsToUploadList(response?.data?.attachments || [])
}

// ---------------------------------------------------------------------------
// Static options
// ---------------------------------------------------------------------------
export const categoryOptions = [
  'Payroll and employee benefits',
  'Employee engagement',
  'Employee government remittances',
  'Taxes, Licenses and Permits',
  'Marketing',
  'Store - Rent, security agency, IT and communications',
  'Store - Office supplies',
  'HO - Rent, security agency, IT and communications',
  'HO - Office supplies',
  'Professional fees, consultants',
  'Inventory - FOB',
  'Inventory - shipping and duties',
  'Warehouse',
  'Employee reimbursements',
  'Cash advances and liquidation',
  'Store - Petty cash set up and replenishment',
  'HO - Petty cash set up and replenishment',
  'Store capex',
  'HO capex',
  'Store - Change Fund set up'
]

export const costCenterOptions = [
  'P5101ST (Glorietta)',
  'P5102ST (Alabang Town Center)',
  'P5103ST (Trinoma)',
  'P5104ST (Manila Bay)',
  'P5105ST (Feliz)',
  'P5106ST (Gateway)',
  'P5107ST (Festival)',
  'P5108ST (Nuvali)',
  'P5109ST (Evo City)',
  'PPHPACM (Country Manager)',
  'PPHPAEO (Executive Office)',
  'PPHPAFN (Finance)',
  'PPHPARR (Human Resources)',
  'PPHPASC (Supply Chain)',
  'PPHPMKT (Marketing)',
  'PPHPOPS (Operations)'
]

export const paymentMethodOptions = [
  'Bank Transfer',
  'Cheque',
  'Credit Card',
  'Cash Delivery by Bank'
]

// ---------------------------------------------------------------------------
// Currency options – built from master list in currencyUtils.js
// ---------------------------------------------------------------------------
export const currencyOptions = [
  { value: 'PHP', label: 'PHP (₱)', symbol: '₱' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
  { value: 'Others', label: 'Others', symbol: '' }
]

// ---------------------------------------------------------------------------
// Helpers for currency handling
// ---------------------------------------------------------------------------
export const getCurrencySymbol = (selectedCurrency, showCustomCurrency, form) => {
  if (showCustomCurrency) {
    const custom = form.getFieldValue('currency')?.trim().toUpperCase()
    if (custom && custom.length === 3 && isValidCurrencyCode(custom)) {
      return `${getSymbol(custom)} `
    }
    return ''
  }

  return getSymbol(selectedCurrency)
}
export const handleCurrencyChange = (value, setSelectedCurrency, setShowCustomCurrency, form) => {
  setSelectedCurrency(value)
  setShowCustomCurrency(value === 'Others')

  if (value === 'Others') {
    form.setFieldsValue({ currency: '' })
  } else {
    form.setFieldsValue({ currency: value })
  }
}

// ---------------------------------------------------------------------------
// Currency validator – used by Ant Form
// ---------------------------------------------------------------------------
export const currencyValidator = async (_, value) => {
  if (!value) {
    return Promise.reject(new Error('Currency is required'))
  }

  const code = value.trim().toUpperCase()

  if (code.length !== 3) {
    return Promise.reject(new Error('Currency code must be exactly 3 letters'))
  }

  if (!isValidCurrencyCode(code)) {
    return Promise.reject(new Error('Invalid currency code. Must be a valid ISO 4217 code.'))
  }

  return Promise.resolve()
}

// ---------------------------------------------------------------------------
// Vendor fetching
// ---------------------------------------------------------------------------
export const fetchVendors = async (msalInstance, setVendorOptions, setVendorsLoading) => {
  try {
    setVendorsLoading(true)

    const response = await fetchVendorList(msalInstance)

    if (response && response.data) {
      const activeVendors = response.data
        .filter(vendor => vendor.active === true)
        .map(vendor => ({
          value: vendor._id,
          label: vendor.vendorName,
          address: vendor.vendorAddress,
          vendorId: vendor.vendorID,
          _id: vendor._id,
          tinNo: vendor.tinNumber
        }))
        .sort((a, b) => a.label.localeCompare(b.label))  // ← alphabetical sort

      setVendorOptions(activeVendors)
    }
  } catch (error) {
    console.error('Error fetching vendors:', error)
    const errorMessage = error?.response?.data?.message ||
      error?.message ||
      'Failed to load vendors. Please try again.'
    message.error(errorMessage)
    setVendorOptions([])
  } finally {
    setVendorsLoading(false)
  }
}

// ---------------------------------------------------------------------------
// Draft data loading (create & edit)
// ---------------------------------------------------------------------------
export const loadDraftData = (
  editingDraft,
  visible,
  vendorOptions,
  form,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency
) => {
  if (editingDraft && visible && vendorOptions.length > 0) {
    const draftData = editingDraft.formData || editingDraft

    // ---- Vendor resolution -------------------------------------------------
    let vendorId = draftData.vendorName
    let vendorAddress = draftData.vendorAddress
    let vendorUID = draftData.vendorId
    let tinNo = draftData.tinNo

    if (editingDraft.selectedVendor && typeof editingDraft.selectedVendor === 'object') {
      const selectedVendor = editingDraft.selectedVendor
      vendorId = selectedVendor._id
      vendorAddress = selectedVendor.vendorAddress
      vendorUID = selectedVendor.vendorID
      tinNo = selectedVendor.tinNumber
    } else if (draftData.vendorDisplayName || (typeof draftData.vendorName === 'string' && !draftData.vendorName.match(/^[a-f\d]{24}$/i))) {
      const vendorOption = vendorOptions.find(v =>
        v.label === (draftData.vendorDisplayName || draftData.vendorName)
      )
      if (vendorOption) {
        vendorId = vendorOption.value
        vendorAddress = vendorOption.address
        vendorUID = vendorOption.vendorId
        tinNo = vendorOption.tinNo
      }
    }

    // ---- Date -------------------------------------------------------------
    let dateOfCreation = dayjs()
    if (editingDraft.transactionDateTime) {
      dateOfCreation = dayjs(editingDraft.transactionDateTime)
    } else if (draftData.dateOfCreation) {
      if (typeof draftData.dateOfCreation === 'string') {
        if (draftData.dateOfCreation.includes('T')) {
          dateOfCreation = dayjs(draftData.dateOfCreation)
        } else if (draftData.dateOfCreation.includes('-') && draftData.dateOfCreation.split('-')[2]?.length === 4) {
          dateOfCreation = dayjs(draftData.dateOfCreation, 'DD-MM-YYYY')
        } else {
          dateOfCreation = dayjs(draftData.dateOfCreation)
        }
      } else {
        dateOfCreation = dayjs(draftData.dateOfCreation)
      }
    }

    // ---- Currency ---------------------------------------------------------
    let currency = editingDraft.currencyType || draftData.currency || 'PHP'

    if (editingDraft.currencyType === 'Others' && editingDraft.otherCurrencyType) {
      currency = editingDraft.otherCurrencyType
    } else if (editingDraft.otherCurrencyType && !editingDraft.currencyType) {
      currency = editingDraft.otherCurrencyType
    }

    if (draftData.currencyType === 'Others' && draftData.otherCurrencyType) {
      currency = draftData.otherCurrencyType
    }

    const requestedAmount = editingDraft.amount ? parseFloat(editingDraft.amount) : (draftData.requestedAmount || 0)

    const formValues = {
      vendorName: vendorId,
      vendorAddress,
      vendorId: vendorUID,
      tinNo,
      invoiceReference: editingDraft.invoiceReferenceNumber || draftData.invoiceReference,
      sapReferenceId: editingDraft.sapReferenceId || draftData.sapReferenceId,
      dateOfCreation,
      categories: editingDraft.transactionCategories || draftData.categories,
      currency,
      requestedAmount,
      filePreparedFor: editingDraft.filePreparedFor || draftData.filePreparedFor,
      preferredPaymentMethod: editingDraft.preferredPaymentMethod || draftData.preferredPaymentMethod
    }

    // ---- Cost centers -----------------------------------------------------
    let costCenterData = []

    if (editingDraft.costCenterPurposeList && Array.isArray(editingDraft.costCenterPurposeList)) {
      costCenterData = editingDraft.costCenterPurposeList
    } else if (draftData.costCenterData && Array.isArray(draftData.costCenterData)) {
      costCenterData = draftData.costCenterData
    } else if (draftData.costCenter || draftData.purpose) {
      costCenterData = [{
        costCenter: draftData.costCenter,
        purpose: draftData.purpose
      }]
    }

    if (costCenterData.length > 0) {
      const newFields = costCenterData.map((_, index) => ({ id: index + 1 }))
      setCostCenterFields(newFields)

      costCenterData.forEach((item, index) => {
        formValues[`costCenter_${index + 1}`] = item.costCenter
        formValues[`purpose_${index + 1}`] = item.purpose
      })
    } else {
      setCostCenterFields([{ id: 1 }])
    }

    // ---- Currency UI state -------------------------------------------------
    if (currency) {
      const isCustom = !currencyOptions.some(o => o.value === currency)
      setSelectedCurrency(isCustom ? 'Others' : currency)
      setShowCustomCurrency(isCustom)
    }

    // ---- Attachments -------------------------------------------------------
    const attachments = Array.isArray(editingDraft.attachments)
      ? editingDraft.attachments
      : (Array.isArray(draftData.attachments) ? draftData.attachments : [])
    const uploadAttachments = mapAttachmentsToUploadList(attachments)
    formValues.attachments = uploadAttachments
    setAttachmentFileList(uploadAttachments)

    form.setFieldsValue(formValues)

    // ---- Fallback vendor lookup if address missing -------------------------
    if (vendorId && !vendorAddress) {
      const vendor = vendorOptions.find(v => v.value === vendorId)
      if (vendor) {
        setTimeout(() => {
          form.setFieldsValue({
            vendorAddress: vendor.address,
            vendorId: vendor.vendorId,
            tinNo: vendor.tinNo
          })
        }, 100)
      }
    }
  } else if (!editingDraft && visible) {
    form.resetFields()
    setAttachmentFileList([])
    setCostCenterFields([{ id: 1 }])
    setSelectedCurrency('PHP')
    setShowCustomCurrency(false)
    form.setFieldsValue({
      dateOfCreation: dayjs(),
      currency: 'PHP'
    })
  }
}

// ---------------------------------------------------------------------------
// Cost-center field management
// ---------------------------------------------------------------------------
export const addCostCenterField = (costCenterFields, setCostCenterFields) => {
  const newId = Math.max(...costCenterFields.map(f => f.id), 0) + 1
  setCostCenterFields([...costCenterFields, { id: newId }])
}

export const removeCostCenterField = (id, costCenterFields, setCostCenterFields, form) => {
  if (costCenterFields.length > 1) {
    setCostCenterFields(costCenterFields.filter(f => f.id !== id))
    const fieldsToRemove = [`costCenter_${id}`, `purpose_${id}`]
    const current = form.getFieldsValue()
    fieldsToRemove.forEach(f => delete current[f])
    form.setFieldsValue(current)
  }
}

export const handleVendorChange = (value, vendorOptions, form) => {
  const vendor = vendorOptions.find(v => v.value === value)
  if (vendor) {
    form.setFieldsValue({
      vendorAddress: vendor.address,
      vendorId: vendor.vendorId,
      tinNo: vendor.tinNo
    })
  }
}

// ---------------------------------------------------------------------------
// Collect cost-center data from dynamic fields
// ---------------------------------------------------------------------------
export const collectCostCenterData = (costCenterFields, values) => {
  return costCenterFields
    .map(field => ({
      costCenter: values[`costCenter_${field.id}`] || '',
      purpose: values[`purpose_${field.id}`] || ''
    }))
    .filter(item => item.costCenter && item.purpose)
}

// ---------------------------------------------------------------------------
// Payload builders
// ---------------------------------------------------------------------------
export const createApiPayload = (values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList, saveAsDraft = false) => {
  const isCustomCurrency = !['PHP', 'AUD', 'USD'].includes(finalCurrency)

  // Format date to ISO string - use start of day in local timezone
  const formattedDate = values.dateOfCreation
    ? dayjs(values.dateOfCreation).startOf('day').toISOString()
    : dayjs().startOf('day').toISOString()

  const payload = {
    selectedVendor: selectedVendorData?.value || selectedVendorData?._id || values.vendorName,
    invoiceReferenceNumber: values.invoiceReference || '',
    sapReferenceId: values.sapReferenceId || '',
    transactionDateTime: formattedDate,
    transactionCategories: values.categories,
    currencyType: isCustomCurrency ? 'Others' : finalCurrency,
    amount: values.requestedAmount?.toString(),
    filePreparedFor: values.filePreparedFor,
    preferredPaymentMethod: values.preferredPaymentMethod,
    costCenterPurposeList: JSON.stringify(costCenterData.map(item => ({
      costCenter: item.costCenter,
      purpose: item.purpose
    }))),
    saveAsDraft,
    attachments: attachmentFileList
  }

  if (isCustomCurrency) {
    payload.otherCurrencyType = finalCurrency
  }

  return payload
}

export const createDraftApiPayload = (values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList) => {
  const payload = createApiPayload(values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList, true)
  delete payload.sapReferenceId
  return payload
}

export const createFormattedResponse = (values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList, response) => {
  return {
    ...values,
    vendorName: selectedVendorData?.label || values.vendorName,
    dateOfCreation: values.dateOfCreation?.format('DD-MM-YYYY'),
    currency: finalCurrency,
    attachments: attachmentFileList,
    status: 'Unpaid',
    rfpId: response.transactionId || (Math.floor(Math.random() * 9000) + 1000).toString(),
    costCenterData,
    costCenter: costCenterData[0]?.costCenter || '',
    purpose: costCenterData[0]?.purpose || '',
    apiResponse: response
  }
}

export const createCompleteFormData = (values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList) => {
  return {
    ...values,
    vendorName: values.vendorName,
    vendorDisplayName: selectedVendorData?.label || values.vendorName,
    vendorAddress: values.vendorAddress,
    vendorId: values.vendorId,
    tinNo: values.tinNo,
    dateOfCreation: values.dateOfCreation?.format('DD-MM-YYYY') || dayjs().format('DD-MM-YYYY'),
    currency: finalCurrency,
    attachments: [...attachmentFileList],
    costCenterData,
    costCenter: costCenterData[0]?.costCenter || '',
    purpose: costCenterData[0]?.purpose || '',
    sapReferenceId: values.sapReferenceId || ''
  }
}

export const createDraftValues = (editingDraft, selectedVendorData, values, finalCurrency, response, completeFormData) => {
  return {
    id: editingDraft?.id || Date.now(),
    vendorName: selectedVendorData?.label || values.vendorName,
    dateOfCreation: values.dateOfCreation?.format('DD-MM-YYYY') || dayjs().format('DD-MM-YYYY'),
    dateCreated: values.dateOfCreation?.format('DD-MM-YYYY') || dayjs().format('DD-MM-YYYY'),
    currency: finalCurrency,
    requestedAmount: values.requestedAmount || 0,
    categories: values.categories || '',
    status: 'Draft',
    lastModified: new Date().toLocaleString(),
    rfpId: response.transactionId || editingDraft?.rfpId || `DRAFT-${String(Date.now()).slice(-3)}`,
    apiResponse: response,
    formData: completeFormData
  }
}

// ---------------------------------------------------------------------------
// Form state reset
// ---------------------------------------------------------------------------
export const resetFormState = (
  form,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency
) => {
  form.resetFields()
  setAttachmentFileList([])
  setCostCenterFields([{ id: 1 }])
  setSelectedCurrency('PHP')
  setShowCustomCurrency(false)
}

// ---------------------------------------------------------------------------
// Upload props
// ---------------------------------------------------------------------------
export const getUploadProps = (attachmentFileList, setAttachmentFileList) => {
  return {
    fileList: attachmentFileList,
    beforeUpload: () => false,
    onChange: ({ fileList: newFileList }) => {
      setAttachmentFileList(newFileList)
    },
    onRemove: (file) => {
      setAttachmentFileList(attachmentFileList.filter(item => item.uid !== file.uid))
    },
    multiple: true
  }
}

// ---------------------------------------------------------------------------
// Submit (new RFP)
// ---------------------------------------------------------------------------
export const handleSubmit = async (
  values,
  costCenterFields,
  vendorOptions,
  attachmentFileList,
  msalInstance,
  onSubmit,
  form,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency,
  setLoading
) => {
  try {
    setLoading(true)

    const costCenterData = collectCostCenterData(costCenterFields, values)
    if (costCenterData.length === 0) {
      message.error('Please fill in at least one cost center and purpose')
      return
    }

    const selectedVendorData = vendorOptions.find(v => v.value === values.vendorName)
    const finalCurrency = values.currency

    const apiPayload = createApiPayload(values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList, false)
    const response = await createRFP(apiPayload, msalInstance)
    const formattedResponse = createFormattedResponse(values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList, response)

    await onSubmit(formattedResponse)
    resetFormState(form, setAttachmentFileList, setCostCenterFields, setSelectedCurrency, setShowCustomCurrency)

    message.success(response?.message || response?.data?.message || 'RFP created successfully!')
  } catch (error) {
    console.error('Error submitting RFP form:', error)
    
    // Display appropriate error message
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
      message.error(`Failed to create RFP: ${errorMessage}`)
    } else if (error.request) {
      message.error('Content is too large. Please reduce the file size and try again.')
    } else {
      message.error('Failed to create RFP: ' + (error.message || 'Unknown error'))
    }
  } finally {
    setLoading(false)
  }
}

// ---------------------------------------------------------------------------
// Save as Draft (new)
// ---------------------------------------------------------------------------
export const handleSaveAsDraft = async (
  form,
  costCenterFields,
  vendorOptions,
  attachmentFileList,
  editingDraft,
  msalInstance,
  onSaveAsDraft,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency,
  setSavingDraft
) => {
  try {
    setSavingDraft(true)

    const values = form.getFieldsValue()
    const costCenterData = collectCostCenterData(costCenterFields, values)

    if (costCenterData.length === 0) {
      message.error('Please fill in at least one cost center and purpose')
      return
    }

    const selectedVendorData = vendorOptions.find(v => v.value === values.vendorName)
    const finalCurrency = values.currency

    const completeFormData = createCompleteFormData(values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList)
    const apiPayload = createDraftApiPayload(values, selectedVendorData, finalCurrency, costCenterData, attachmentFileList)

    const response = await createRFP(apiPayload, msalInstance)
    const draftValues = createDraftValues(editingDraft, selectedVendorData, values, finalCurrency, response, completeFormData)

    await onSaveAsDraft(draftValues)
    resetFormState(form, setAttachmentFileList, setCostCenterFields, setSelectedCurrency, setShowCustomCurrency)

    message.success(response?.message || response?.data?.message || 'Draft saved successfully!')
  } catch (error) {
    console.error('Error saving draft:', error)
    
    // Display appropriate error message
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
      message.error(`Failed to save draft: ${errorMessage}`)
    } else if (error.request) {
      message.error('Content is too large. Please reduce the file size and try again.')
    } else {
      message.error('Failed to save draft: ' + (error.message || 'Unknown error'))
    }
  } finally {
    setSavingDraft(false)
  }
}

// ---------------------------------------------------------------------------
// Save edited draft
// ---------------------------------------------------------------------------
const hasRequiredAttachment = (attachmentFileList) => {
  // attachmentFileList is kept in sync with the server on every add/remove
  // for existing drafts, so it's the source of truth - editingDraft.attachments
  
  return attachmentFileList.length > 0
}

export const handleSaveEditedDraft = async (
  form,
  costCenterFields,
  vendorOptions,
  attachmentFileList,
  editingDraft,
  msalInstance,
  onSaveAsDraft,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency,
  setSavingDraft
) => {
  try {
    setSavingDraft(true)

    if (!hasRequiredAttachment(attachmentFileList)) {
      message.error('Please upload at least one attachment before saving this draft.')
      return
    }

    const values = form.getFieldsValue()
    const costCenterPurposeList = costCenterFields.map(field => ({
      costCenter: values[`costCenter_${field.id}`] || '',
      purpose: values[`purpose_${field.id}`] || ''
    })).filter(item => item.costCenter && item.purpose)

    const selectedVendor = vendorOptions.find(v => v.value === values.vendorName)

    const isCustomCurrency = !['AUD', 'USD', 'PHP'].includes(values.currency) && values.currency !== 'Others'

    const originalDraft = editingDraft.formData || editingDraft
    const originalValues = {
      transactionDateTime: originalDraft.transactionDateTime || '',
      transactionCategories: originalDraft.transactionCategories || '',
      invoiceReferenceNumber: originalDraft.invoiceReferenceNumber || '',
      sapReferenceId: originalDraft.sapReferenceId || '',
      currencyType: originalDraft.currencyType || '',
      otherCurrencyType: originalDraft.otherCurrencyType || '',
      amount: originalDraft.amount || '',
      filePreparedFor: originalDraft.filePreparedFor || '',
      preferredPaymentMethod: originalDraft.preferredPaymentMethod || '',
      selectedVendor: originalDraft.selectedVendor || '',
      costCenterPurposeList: Array.isArray(originalDraft.costCenterPurposeList)
        ? originalDraft.costCenterPurposeList
        : (originalDraft.costCenterPurposeList ? JSON.parse(originalDraft.costCenterPurposeList) : [])
    }

    const updates = {}

    // Format date properly for comparison
    const newDateFormatted = values.dateOfCreation ? dayjs(values.dateOfCreation).startOf('day').toISOString() : ''
    const originalDateFormatted = originalValues.transactionDateTime ? dayjs(originalValues.transactionDateTime).startOf('day').toISOString() : ''

    if (newDateFormatted !== originalDateFormatted) {
      updates.transactionDateTime = newDateFormatted
    }

    if (values.categories !== originalValues.transactionCategories) {
      updates.transactionCategories = values.categories || ''
    }

    if (values.invoiceReference !== originalValues.invoiceReferenceNumber) {
      updates.invoiceReferenceNumber = values.invoiceReference || ''
    }

    const currentCurrencyType = isCustomCurrency ? 'Others' : values.currency
    const originalCurrencyType = originalValues.currencyType
    const originalOtherCurrencyType = originalValues.otherCurrencyType

    const currencyChanged = currentCurrencyType !== originalCurrencyType ||
      (isCustomCurrency && values.currency !== originalOtherCurrencyType) ||
      (!isCustomCurrency && originalOtherCurrencyType !== '')

    if (currencyChanged) {
      updates.currencyType = currentCurrencyType
      if (isCustomCurrency) {
        updates.otherCurrencyType = values.currency
      } else if (originalOtherCurrencyType !== '') {
        updates.otherCurrencyType = ''
      }
    }

    if (String(values.requestedAmount) !== originalValues.amount) {
      updates.amount = values.requestedAmount ? String(values.requestedAmount) : ''
    }

    if (values.filePreparedFor !== originalValues.filePreparedFor) {
      updates.filePreparedFor = values.filePreparedFor || ''
    }

    if (values.preferredPaymentMethod !== originalValues.preferredPaymentMethod) {
      updates.preferredPaymentMethod = values.preferredPaymentMethod || ''
    }

    const currentVendorId = selectedVendor ? selectedVendor.value : values.vendorName
    let originalVendorId = ''
    if (originalValues.selectedVendor && typeof originalValues.selectedVendor === 'string') {
      try {
        const parsed = JSON.parse(originalValues.selectedVendor)
        originalVendorId = parsed.vendorId || parsed._id || ''
      } catch {
        originalVendorId = originalValues.selectedVendor
      }
    } else if (originalValues.selectedVendor && typeof originalValues.selectedVendor === 'object') {
      originalVendorId = originalValues.selectedVendor._id || originalValues.selectedVendor.vendorId || ''
    }

    if (currentVendorId !== originalVendorId) {
      updates.selectedVendor = currentVendorId
    }

    const currentCostCentersString = JSON.stringify(costCenterPurposeList.map(item => ({
      costCenter: item.costCenter,
      purpose: item.purpose
    })))
    const originalCostCentersString = JSON.stringify(originalValues.costCenterPurposeList.map(item => ({
      costCenter: item.costCenter,
      purpose: item.purpose
    })))

    if (currentCostCentersString !== originalCostCentersString) {
      updates.costCenterPurposeList = costCenterPurposeList
    }

    updates.saveAsDraft = 'true'

    // Never send sapReferenceId for drafts
    delete updates.sapReferenceId

    if (Object.keys(updates).length === 1 && updates.saveAsDraft) {
      message.info('No changes detected. Draft is already up to date.')
      return
    }

    const transactionId = editingDraft.transactionId || editingDraft._id
    if (!transactionId) throw new Error('Transaction ID not found in draft data')

    const result = await editRFP(transactionId, true, updates, msalInstance)
    message.success('Draft updated successfully!')

    resetFormState(form, setAttachmentFileList, setCostCenterFields, setSelectedCurrency, setShowCustomCurrency)

    if (onSaveAsDraft) {
      onSaveAsDraft(result)
    }
  } catch (error) {
    console.error('Error saving edited draft:', error)
    
    // Display appropriate error message
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
      message.error(`Failed to save draft: ${errorMessage}`)
    } else if (error.request) {
      message.error('Content is too large. Please reduce the file size and try again.')
    } else {
      message.error('Failed to save draft: ' + (error.message || 'Unknown error'))
    }
  } finally {
    setSavingDraft(false)
  }
}

// ---------------------------------------------------------------------------
// Submit edited draft (final submit)
// ---------------------------------------------------------------------------
export const handleSubmitEditedDraft = async (
  values,
  costCenterFields,
  vendorOptions,
  attachmentFileList,
  editingDraft,
  msalInstance,
  onSubmit,
  form,
  setAttachmentFileList,
  setCostCenterFields,
  setSelectedCurrency,
  setShowCustomCurrency,
  setLoading
) => {
  try {
    setLoading(true)

    if (!hasRequiredAttachment(attachmentFileList)) {
      message.error('Please upload at least one attachment before submitting this draft.')
      return
    }

    const transactionId = editingDraft.transactionId || editingDraft._id
    if (!transactionId) throw new Error('Transaction ID not found in draft data')

    // Collect all form values to check what changed
    const costCenterPurposeList = costCenterFields.map(field => ({
      costCenter: values[`costCenter_${field.id}`] || '',
      purpose: values[`purpose_${field.id}`] || ''
    })).filter(item => item.costCenter && item.purpose)

    const selectedVendor = vendorOptions.find(v => v.value === values.vendorName)
    const isCustomCurrency = !['AUD', 'USD', 'PHP'].includes(values.currency) && values.currency !== 'Others'

    // Get original values
    const originalDraft = editingDraft.formData || editingDraft
    const originalValues = {
      transactionDateTime: originalDraft.transactionDateTime || '',
      transactionCategories: originalDraft.transactionCategories || '',
      invoiceReferenceNumber: originalDraft.invoiceReferenceNumber || '',
      currencyType: originalDraft.currencyType || '',
      otherCurrencyType: originalDraft.otherCurrencyType || '',
      amount: originalDraft.amount || '',
      filePreparedFor: originalDraft.filePreparedFor || '',
      preferredPaymentMethod: originalDraft.preferredPaymentMethod || '',
      selectedVendor: originalDraft.selectedVendor || '',
      costCenterPurposeList: Array.isArray(originalDraft.costCenterPurposeList)
        ? originalDraft.costCenterPurposeList
        : (originalDraft.costCenterPurposeList ? JSON.parse(originalDraft.costCenterPurposeList) : [])
    }

    // Build updates object with all fields that may have changed
    const updates = {
      saveAsDraft: 'false',
      sapReferenceId: values.sapReferenceId || 'NA'
    }

    // Check and add date if changed
    const newDateFormatted = values.dateOfCreation ? dayjs(values.dateOfCreation).startOf('day').toISOString() : ''
    const originalDateFormatted = originalValues.transactionDateTime ? dayjs(originalValues.transactionDateTime).startOf('day').toISOString() : ''

    if (newDateFormatted && newDateFormatted !== originalDateFormatted) {
      updates.transactionDateTime = newDateFormatted
    }

    // Check and add other fields if changed
    if (values.categories && values.categories !== originalValues.transactionCategories) {
      updates.transactionCategories = values.categories
    }

    if (values.invoiceReference && values.invoiceReference !== originalValues.invoiceReferenceNumber) {
      updates.invoiceReferenceNumber = values.invoiceReference
    }

    // Check currency changes
    const currentCurrencyType = isCustomCurrency ? 'Others' : values.currency
    const originalCurrencyType = originalValues.currencyType
    const originalOtherCurrencyType = originalValues.otherCurrencyType

    const currencyChanged = currentCurrencyType !== originalCurrencyType ||
      (isCustomCurrency && values.currency !== originalOtherCurrencyType) ||
      (!isCustomCurrency && originalOtherCurrencyType !== '')

    if (currencyChanged) {
      updates.currencyType = currentCurrencyType
      if (isCustomCurrency) {
        updates.otherCurrencyType = values.currency
      } else if (originalOtherCurrencyType !== '') {
        updates.otherCurrencyType = ''
      }
    }

    // Check amount change
    if (values.requestedAmount && String(values.requestedAmount) !== originalValues.amount) {
      updates.amount = String(values.requestedAmount)
    }

    // Check file prepared for change
    if (values.filePreparedFor && values.filePreparedFor !== originalValues.filePreparedFor) {
      updates.filePreparedFor = values.filePreparedFor
    }

    // Check payment method change
    if (values.preferredPaymentMethod && values.preferredPaymentMethod !== originalValues.preferredPaymentMethod) {
      updates.preferredPaymentMethod = values.preferredPaymentMethod
    }

    // Check vendor change
    const currentVendorId = selectedVendor ? selectedVendor.value : values.vendorName
    let originalVendorId = ''
    if (originalValues.selectedVendor && typeof originalValues.selectedVendor === 'string') {
      try {
        const parsed = JSON.parse(originalValues.selectedVendor)
        originalVendorId = parsed.vendorId || parsed._id || ''
      } catch {
        originalVendorId = originalValues.selectedVendor
      }
    } else if (originalValues.selectedVendor && typeof originalValues.selectedVendor === 'object') {
      originalVendorId = originalValues.selectedVendor._id || originalValues.selectedVendor.vendorId || ''
    }

    if (currentVendorId && currentVendorId !== originalVendorId) {
      updates.selectedVendor = currentVendorId
    }

    // Check cost center changes
    const currentCostCentersString = JSON.stringify(costCenterPurposeList.map(item => ({
      costCenter: item.costCenter,
      purpose: item.purpose
    })))
    const originalCostCentersString = JSON.stringify(originalValues.costCenterPurposeList.map(item => ({
      costCenter: item.costCenter,
      purpose: item.purpose
    })))

    if (currentCostCentersString !== originalCostCentersString) {
      updates.costCenterPurposeList = costCenterPurposeList
    }

    const result = await editRFP(transactionId, true, updates, msalInstance)
    message.success('RFP submitted successfully!')

    resetFormState(form, setAttachmentFileList, setCostCenterFields, setSelectedCurrency, setShowCustomCurrency)

    if (onSubmit) {
      onSubmit(result)
    }
  } catch (error) {
    console.error('Error submitting edited RFP:', error)
    
    // Display appropriate error message
    if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
      message.error(`Failed to submit RFP: ${errorMessage}`)
    } else if (error.request) {
      message.error('Content is too large. Please reduce the file size and try again.')
    } else {
      message.error('Failed to submit RFP: ' + (error.message || 'Unknown error'))
    }
  } finally {
    setLoading(false)
  }
}
